import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, Clock, Fuel, RefreshCw } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { DataTable } from '../components/common/DataTable';
import { HashDisplay } from '../components/common/HashDisplay';
import { StatCard } from '../components/common/StatCard';
import { LiveIndicator } from '../components/common/LiveIndicator';
import { getMempool } from '../lib/api';
import { formatNumber, timeAgo } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

export default function MempoolPage() {
  const [mempool, setMempool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  const fetchData = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const res = await getMempool(100);
      setMempool(res.data);
    } catch (error) {
      console.error('Error fetching mempool:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 5000);
    return () => clearInterval(interval);
  }, []);

  const totalFees = mempool.reduce((acc, tx) => acc + tx.fee, 0);
  const totalValue = mempool.reduce((acc, tx) => acc + tx.value, 0);
  const avgGasPrice = mempool.length > 0 
    ? mempool.reduce((acc, tx) => acc + tx.gas_price, 0) / mempool.length 
    : 0;

  const columns = [
    {
      header: 'Hash',
      cell: (row) => <HashDisplay hash={row.hash} />,
    },
    {
      header: 'Type',
      cell: (row) => (
        <Badge variant="outline" className="text-yellow-400 border-yellow-400/30">
          {row.type}
        </Badge>
      ),
    },
    {
      header: 'From',
      cell: (row) => <HashDisplay hash={row.from_address} type="address" link={`/address/${row.from_address}`} />,
    },
    {
      header: 'To',
      cell: (row) => <HashDisplay hash={row.to_address} type="address" link={`/address/${row.to_address}`} />,
    },
    {
      header: 'Value',
      cell: (row) => (
        <span className="text-primary font-mono">
          {formatNumber(row.value, 4)} GG
        </span>
      ),
    },
    {
      header: 'Gas Price',
      cell: (row) => (
        <span className="font-mono text-sm">
          {row.gas_price} Gwei
        </span>
      ),
    },
    {
      header: 'Fee',
      cell: (row) => (
        <span className="text-muted-foreground font-mono text-sm">
          {row.fee.toFixed(6)} GG
        </span>
      ),
    },
    {
      header: 'Pending',
      cell: (row) => <span className="text-muted-foreground text-sm">{timeAgo(row.pending_since)}</span>,
    },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="mempool-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500/20 to-transparent">
              <Database className="w-6 h-6 text-yellow-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Mempool</h1>
              <p className="text-sm text-muted-foreground">
                {mempool.length} pending transactions
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <LiveIndicator label="Updating" />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Pending Transactions"
            value={formatNumber(mempool.length, 0)}
            icon={Database}
            accentColor="warning"
          />
          <StatCard
            title="Total Value"
            value={`${formatNumber(totalValue, 2)} GG`}
            icon={Clock}
            accentColor="primary"
          />
          <StatCard
            title="Total Fees"
            value={`${totalFees.toFixed(4)} GG`}
            icon={Fuel}
            accentColor="success"
          />
          <StatCard
            title="Avg Gas Price"
            value={`${formatNumber(avgGasPrice, 0)} Gwei`}
            icon={Fuel}
            accentColor="secondary"
          />
        </div>

        {/* Table */}
        <DataTable
          columns={columns}
          data={mempool}
          loading={loading}
          emptyMessage="Mempool is empty"
        />
      </div>
    </Layout>
  );
}
